import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { RadioButton } from "primereact/radiobutton";
import { Toast } from "primereact/toast";
import React, { useRef, useState } from "react";
import { Controller, Path, useForm } from "react-hook-form";
import {
    appSectionCardStyle,
    renderSurfaceHeader,
} from "../../../../components/general/uiSurfaceStyles";
import { useNurseFormAutoSave } from "../useNurseFormAutoSave";

export interface Step11FormValues {
    // Positioning
    positioningLevel: "independent" | "standby" | "guideLimbs" | "onePersonAssist" | "onePersonFull" | "twoPersonFull" | "";
    repositionHoursDay: string;
    repositionHoursNight: string;
    positioningDrawSheet: boolean; positioningHospitalBed: boolean; positioningSpecialMattress: boolean;
    positioningTrapeze: boolean; positioningWedge: boolean; positioningFootCradle: boolean;
    positioningBedRails: boolean; positioningOther: string; positioningComments: string;
    // Transfers
    transfersLevel: "independent" | "standby" | "guideLimbs" | "onePersonAssist" | "onePersonFull" | "twoPersonFull" | "";
    transfersMechanical: boolean; transfersComments: string;
    // Personal Hygiene
    hygieneLevel: "independent" | "setup" | "monitoring" | "handsOn" | "unable" | "";
    hygieneSkinPrecautions: string; hygieneLotions: string; hygieneNailCare: string; hygieneComments: string;
    // Dressing
    dressingLevel: "independent" | "setup" | "monitoring" | "laidOut" | "shoesSocks" | "guideLimbs" | "caregiverOne" | "caregiverTwo" | "";
    dressingComments: string;
    // Ambulation
    ambulationLevel: "independent" | "assistiveDevice" | "standby" | "steadying" | "onePersonWeight" | "twoPersonWeight" | "wheelchairAssist" | "wheelchairIndependent" | "bedBound" | "";
    ambulationDistanceLimit: string;
    ambulationComments: string;
    // Stairs
    stairsLevel: "withoutAssist" | "standbyAssist" | "assistiveDevice" | "unableToAssess" | "unable" | "";
    stairsComments: string;
    // Assistive Devices
    deviceNone: boolean; deviceCaneRegular: boolean; deviceCaneQuad: boolean; deviceCrutches: boolean;
    deviceGaitBelt: boolean; deviceProsthesis: boolean; deviceTransportChair: boolean;
    deviceWalkerTwo: boolean; deviceWalkerFour: boolean; deviceWheelchairRegular: boolean;
    deviceWheelchairElectric: boolean; deviceOther: string; deviceComments: string;
    // Toileting
    toiletingLevel: "independent" | "setup" | "monitoring" | "assistCleansing" | "liftingOne" | "liftingTwo" | "dependentOne" | "dependentTwo" | "";
    toiletingNightFrequency: string;
    toiletingInappropriatePlaces: boolean;
    toiletingComments: string;
    // Bowel Management
    bowelTrainingProgram: boolean; bowelMeds: string; bowelEnema: boolean;
    bowelSuppository: boolean; bowelConstipation: boolean; bowelDiarrhea: boolean;
    bowelOstomy: boolean; bowelComments: string;
    // Bladder Management
    bladderTrainingProgram: boolean; bladderDribbling: boolean; bladderUrgency: boolean;
    bladderStressIncontinence: boolean; bladderUrostomy: boolean;
    bladderPadsDays: boolean; bladderPadsNights: boolean;
    bladderUndergarmentsDays: boolean; bladderUndergarmentsNights: boolean;
    bladderCatheter: boolean; bladderComments: string;
    // Bathing
    bathingLevel: "independent" | "setup" | "monitoring" | "inOutAssist" | "partialAssist" | "fullOne" | "fullTwo" | "";
    bathingTub: boolean; bathingShower: boolean; bathingBedBath: boolean;
    bathingBathBench: boolean; bathingTransferBench: boolean;
    bathingFrequency: string; bathingComments: string;
    // Nutrition
    nutritionLevel: "independent" | "setup" | "monitoring" | "guideFood" | "partialFeed" | "fullFeed" | "";
    nutritionWeightLoss: boolean; nutritionWeightGain: boolean;
    nutritionChokingRisk: boolean;
    nutritionFoodPreferences: string; nutritionFoodAllergies: string;
    nutritionSpecialDiet: string; nutritionSupplements: string; nutritionMechAltered: string;
    nutritionAdaptiveEquipment: string; nutritionComments: string;
    // Therapies
    therapySpeech: boolean; therapyPhysical: boolean; therapyOccupational: boolean;
    therapyMentalHealth: boolean; therapySkilledNursing: boolean; therapyHospice: boolean;
    therapyRespiratory: boolean; therapyCardiovascular: boolean;
    therapyStrengthROM: boolean; therapyPressureUlcers: boolean;
    therapyPainManagement: string; therapyOther: string; therapyComments: string;
    // Medical Treatment
    treatmentNone: boolean; treatmentWoundCare: string; treatmentInfections: string;
    treatmentFeedingTube: boolean; treatmentChemo: boolean; treatmentRadiation: boolean;
    treatmentDialysis: boolean; treatmentSuctioning: boolean; treatmentComments: string;
    // Self-Medication
    medicationLevel: "selfAdmin" | "requiresAssist" | "caregiverAdmin" | "";
    medicationComments: string;
    // Transportation
    transportationLevel: "independent" | "requiresAssist" | "";
    transportationComments: string;
}

export const DEFAULT_STEP11: Step11FormValues = {
    positioningLevel: "", repositionHoursDay: "", repositionHoursNight: "",
    positioningDrawSheet: false, positioningHospitalBed: false, positioningSpecialMattress: false,
    positioningTrapeze: false, positioningWedge: false, positioningFootCradle: false,
    positioningBedRails: false, positioningOther: "", positioningComments: "",
    transfersLevel: "", transfersMechanical: false, transfersComments: "",
    hygieneLevel: "", hygieneSkinPrecautions: "", hygieneLotions: "", hygieneNailCare: "", hygieneComments: "",
    dressingLevel: "", dressingComments: "",
    ambulationLevel: "", ambulationDistanceLimit: "", ambulationComments: "",
    stairsLevel: "", stairsComments: "",
    deviceNone: false, deviceCaneRegular: false, deviceCaneQuad: false, deviceCrutches: false,
    deviceGaitBelt: false, deviceProsthesis: false, deviceTransportChair: false,
    deviceWalkerTwo: false, deviceWalkerFour: false, deviceWheelchairRegular: false,
    deviceWheelchairElectric: false, deviceOther: "", deviceComments: "",
    toiletingLevel: "", toiletingNightFrequency: "", toiletingInappropriatePlaces: false, toiletingComments: "",
    bowelTrainingProgram: false, bowelMeds: "", bowelEnema: false,
    bowelSuppository: false, bowelConstipation: false, bowelDiarrhea: false,
    bowelOstomy: false, bowelComments: "",
    bladderTrainingProgram: false, bladderDribbling: false, bladderUrgency: false,
    bladderStressIncontinence: false, bladderUrostomy: false,
    bladderPadsDays: false, bladderPadsNights: false,
    bladderUndergarmentsDays: false, bladderUndergarmentsNights: false,
    bladderCatheter: false, bladderComments: "",
    bathingLevel: "", bathingTub: false, bathingShower: false, bathingBedBath: false,
    bathingBathBench: false, bathingTransferBench: false, bathingFrequency: "", bathingComments: "",
    nutritionLevel: "", nutritionWeightLoss: false, nutritionWeightGain: false,
    nutritionChokingRisk: false, nutritionFoodPreferences: "", nutritionFoodAllergies: "",
    nutritionSpecialDiet: "", nutritionSupplements: "", nutritionMechAltered: "",
    nutritionAdaptiveEquipment: "", nutritionComments: "",
    therapySpeech: false, therapyPhysical: false, therapyOccupational: false,
    therapyMentalHealth: false, therapySkilledNursing: false, therapyHospice: false,
    therapyRespiratory: false, therapyCardiovascular: false,
    therapyStrengthROM: false, therapyPressureUlcers: false,
    therapyPainManagement: "", therapyOther: "", therapyComments: "",
    treatmentNone: false, treatmentWoundCare: "", treatmentInfections: "",
    treatmentFeedingTube: false, treatmentChemo: false, treatmentRadiation: false,
    treatmentDialysis: false, treatmentSuctioning: false, treatmentComments: "",
    medicationLevel: "", medicationComments: "",
    transportationLevel: "", transportationComments: "",
};

export interface Step11Props {
    sectionData: Partial<Step11FormValues> | null;
    residentId: string;
    formId: string;
    onSave: (data: Step11FormValues) => Promise<void>;
    onNext: () => void;
    onBack: () => void;
    loading?: boolean;
}

const SIX_LEVEL_ADL = [
    { value: "independent", label: "Independently and safely without assistance" },
    { value: "standby", label: "Requires caregiver to standby for safety, cueing, monitoring or encouragement" },
    { value: "guideLimbs", label: "Able but requires hands-on help to guide limbs" },
    { value: "onePersonAssist", label: "Able to assist but requires one person to support" },
    { value: "onePersonFull", label: "Unable to assist: Dependent upon one person" },
    { value: "twoPersonFull", label: "Unable to assist: Dependent upon two or more persons" },
];

const InfoRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
    <div className="col-12 md:col-4 mb-2">
        <div className="text-xs font-medium mb-1" style={{ color: "#6b7280" }}>{label}</div>
        <div className="text-sm font-medium" style={{ color: value ? "#111827" : "#9ca3af" }}>{value || "—"}</div>
    </div>
);

const formatSelected = (items: Array<string | false | null | undefined>) => {
    const selected = items.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
    return selected.length ? selected.join(", ") : undefined;
};

const Step11ActivitiesOfDailyLiving: React.FC<Step11Props> = ({
    sectionData, formId, onSave, onNext, onBack, loading,
}) => {
    const toast = useRef<Toast>(null);
    const [enableEditing, setEnableEditing] = useState<boolean>(false);
    const defaultValues: Step11FormValues = { ...DEFAULT_STEP11, ...(sectionData ?? {}) };
    const { control, handleSubmit, watch, reset } = useForm<Step11FormValues>({ defaultValues });

    const onSubmit = async (data: Step11FormValues) => { await onSave(data); };
    const handleSave = async (data: Step11FormValues) => {
        try {
            await onSave(data);
            toast.current?.show({ severity: "success", summary: "Saved", detail: "ADL assessment saved.", life: 3000 });
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message, life: 4000 });
        }
    };
    const handleSaveAndContinue = async (data: Step11FormValues) => {
        try {
            await onSave(data);
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Saved. Moving to next step.", life: 2000 });
            onNext();
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message, life: 4000 });
        }
    };
    useNurseFormAutoSave({ enabled: enableEditing && !!formId, baselineData: defaultValues, watch, handleSubmit, onSubmit });

    const values = watch();

    const cb = (name: Path<Step11FormValues>, label: string) => (
        <Controller key={name} name={name} control={control} render={({ field: f }) => (
            <div className="flex align-items-center gap-2 mb-1">
                <Checkbox inputId={name} checked={!!f.value} onChange={e => f.onChange(e.checked)} />
                <label htmlFor={name} className="text-sm cursor-pointer">{label}</label>
            </div>
        )} />
    );

    const txt = (name: Path<Step11FormValues>, label: string, placeholder = "") => (
        <div key={name}>
            <label className="font-medium text-sm block mb-1">{label}</label>
            <Controller name={name} control={control} render={({ field: f }) => (
                <InputText {...(f as any)} className="w-full" placeholder={placeholder || label} />
            )} />
        </div>
    );

    const comments = (name: Path<Step11FormValues>) => (
        <Controller key={name} name={name} control={control} render={({ field: f }) => (
            <div className="mt-2">
                <label className="font-medium text-sm block mb-1">Comments</label>
                <InputTextarea {...(f as any)} className="w-full" rows={2} autoResize placeholder="Comments" />
            </div>
        )} />
    );

    const radioLevel = (fieldName: Path<Step11FormValues>, options: Array<{ value: string; label: string }>) => (
        <Controller name={fieldName} control={control} render={({ field: f }) => (
            <div className="flex flex-column gap-1 mb-2">
                {options.map(opt => (
                    <div key={opt.value} className="flex align-items-start gap-2">
                        <RadioButton inputId={`${fieldName}_${opt.value}`} value={opt.value} checked={f.value === opt.value} onChange={() => f.onChange(opt.value)} style={{ marginTop: 2 }} />
                        <label htmlFor={`${fieldName}_${opt.value}`} className="text-sm cursor-pointer">{opt.label}</label>
                    </div>
                ))}
            </div>
        )} />
    );

    return (
        <>
            <Toast ref={toast} />
            <form onSubmit={handleSubmit(handleSave)}>
                <div className="flex justify-content-center gap-2 mb-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>
                {!enableEditing ? (
                    <>
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Activities of Daily Living", "ADL level summary.", "pi pi-list")}
                            <div className="p-3 grid">
                                <InfoRow label="Positioning" value={values.positioningLevel || undefined} />
                                <InfoRow label="Repositioning Schedule" value={formatSelected([
                                    values.repositionHoursDay && `Day: every ${values.repositionHoursDay} hour(s)`,
                                    values.repositionHoursNight && `Night: every ${values.repositionHoursNight} hour(s)`,
                                ])} />
                                <InfoRow label="Positioning Equipment" value={formatSelected([
                                    values.positioningDrawSheet && "Draw sheet",
                                    values.positioningHospitalBed && "Hospital bed",
                                    values.positioningSpecialMattress && "Special mattress",
                                    values.positioningTrapeze && "Trapeze",
                                    values.positioningWedge && "Wedge",
                                    values.positioningFootCradle && "Foot cradle",
                                    values.positioningBedRails && "Bed rails",
                                    values.positioningOther && `Other: ${values.positioningOther}`,
                                ])} />
                                <InfoRow label="Positioning Comments" value={values.positioningComments} />
                                <InfoRow label="Transfers" value={formatSelected([
                                    values.transfersLevel,
                                    values.transfersMechanical && "Mechanical lifting",
                                ])} />
                                <InfoRow label="Transfer Comments" value={values.transfersComments} />
                                <InfoRow label="Personal Hygiene" value={values.hygieneLevel || undefined} />
                                <InfoRow label="Hygiene Details" value={formatSelected([
                                    values.hygieneSkinPrecautions && `Skin precautions: ${values.hygieneSkinPrecautions}`,
                                    values.hygieneLotions && `Lotions/soaps/linens: ${values.hygieneLotions}`,
                                    values.hygieneNailCare && `Nail care: ${values.hygieneNailCare}`,
                                ])} />
                                <InfoRow label="Hygiene Comments" value={values.hygieneComments} />
                                <InfoRow label="Dressing" value={values.dressingLevel || undefined} />
                                <InfoRow label="Dressing Comments" value={values.dressingComments} />
                                <InfoRow label="Ambulation" value={values.ambulationLevel || undefined} />
                                <InfoRow label="Ambulation Details" value={formatSelected([
                                    values.ambulationDistanceLimit && `Distance limit: ${values.ambulationDistanceLimit}`,
                                ])} />
                                <InfoRow label="Ambulation Comments" value={values.ambulationComments} />
                                <InfoRow label="Stairs" value={values.stairsLevel || undefined} />
                                <InfoRow label="Stairs Comments" value={values.stairsComments} />
                                <InfoRow label="Assistive Devices" value={values.deviceNone ? "No equipment used" : formatSelected([
                                    values.deviceCaneRegular && "Regular cane",
                                    values.deviceCaneQuad && "Quad cane",
                                    values.deviceCrutches && "Crutches",
                                    values.deviceGaitBelt && "Gait belt",
                                    values.deviceProsthesis && "Prosthesis",
                                    values.deviceTransportChair && "Transport chair",
                                    values.deviceWalkerTwo && "Two-wheel walker",
                                    values.deviceWalkerFour && "Four-wheel walker",
                                    values.deviceWheelchairRegular && "Regular wheelchair",
                                    values.deviceWheelchairElectric && "Electric wheelchair",
                                    values.deviceOther && `Other: ${values.deviceOther}`,
                                ])} />
                                <InfoRow label="Device Comments" value={values.deviceComments} />
                                <InfoRow label="Toileting" value={formatSelected([
                                    values.toiletingLevel,
                                    values.toiletingNightFrequency && `Night frequency: ${values.toiletingNightFrequency}`,
                                    values.toiletingInappropriatePlaces && "Uses inappropriate places",
                                ])} />
                                <InfoRow label="Toileting Comments" value={values.toiletingComments} />
                                <InfoRow label="Bowel Management" value={formatSelected([
                                    values.bowelTrainingProgram && "Training program",
                                    values.bowelEnema && "Enema",
                                    values.bowelSuppository && "Rectal suppository",
                                    values.bowelConstipation && "Constipation",
                                    values.bowelDiarrhea && "Diarrhea",
                                    values.bowelOstomy && "Ostomy",
                                    values.bowelMeds && `Medications: ${values.bowelMeds}`,
                                ])} />
                                <InfoRow label="Bowel Comments" value={values.bowelComments} />
                                <InfoRow label="Bladder Management" value={formatSelected([
                                    values.bladderTrainingProgram && "Training program",
                                    values.bladderDribbling && "Dribbling",
                                    values.bladderUrgency && "Urgency",
                                    values.bladderStressIncontinence && "Stress incontinence",
                                    values.bladderUrostomy && "Urostomy",
                                    values.bladderCatheter && "Catheter",
                                    values.bladderPadsDays && "Pads days",
                                    values.bladderPadsNights && "Pads nights",
                                    values.bladderUndergarmentsDays && "Undergarments/briefs days",
                                    values.bladderUndergarmentsNights && "Undergarments/briefs nights",
                                ])} />
                                <InfoRow label="Bladder Comments" value={values.bladderComments} />
                                <InfoRow label="Bathing" value={formatSelected([
                                    values.bathingLevel,
                                    values.bathingTub && "Tub",
                                    values.bathingShower && "Shower",
                                    values.bathingBedBath && "Bed bath",
                                    values.bathingBathBench && "Bath bench",
                                    values.bathingTransferBench && "Transfer bench",
                                    values.bathingFrequency && `Frequency: ${values.bathingFrequency}`,
                                ])} />
                                <InfoRow label="Bathing Comments" value={values.bathingComments} />
                                <InfoRow label="Nutrition" value={values.nutritionLevel || undefined} />
                                <InfoRow label="Nutrition Details" value={formatSelected([
                                    values.nutritionWeightLoss && "Weight loss",
                                    values.nutritionWeightGain && "Weight gain",
                                    values.nutritionChokingRisk && "Chewing/swallowing risk",
                                    values.nutritionFoodPreferences && `Food preferences: ${values.nutritionFoodPreferences}`,
                                    values.nutritionFoodAllergies && `Food allergies: ${values.nutritionFoodAllergies}`,
                                    values.nutritionSpecialDiet && `Special diet: ${values.nutritionSpecialDiet}`,
                                    values.nutritionSupplements && `Supplements: ${values.nutritionSupplements}`,
                                    values.nutritionMechAltered && `Mechanically altered: ${values.nutritionMechAltered}`,
                                    values.nutritionAdaptiveEquipment && `Adaptive equipment: ${values.nutritionAdaptiveEquipment}`,
                                ])} />
                                <InfoRow label="Nutrition Comments" value={values.nutritionComments} />
                                <InfoRow label="Therapies" value={formatSelected([
                                    values.therapySpeech && "Speech",
                                    values.therapyPhysical && "Physical",
                                    values.therapyOccupational && "Occupational",
                                    values.therapyMentalHealth && "Mental health",
                                    values.therapySkilledNursing && "Skilled nursing",
                                    values.therapyHospice && "Hospice",
                                    values.therapyRespiratory && "Respiratory",
                                    values.therapyCardiovascular && "Cardiovascular",
                                    values.therapyStrengthROM && "Strength/range of motion",
                                    values.therapyPressureUlcers && "Pressure ulcers",
                                    values.therapyPainManagement && `Pain management: ${values.therapyPainManagement}`,
                                    values.therapyOther && `Other: ${values.therapyOther}`,
                                ])} />
                                <InfoRow label="Therapy Comments" value={values.therapyComments} />
                                <InfoRow label="Medical Treatment" value={values.treatmentNone ? "No medical treatment" : formatSelected([
                                    values.treatmentFeedingTube && "Feeding tube",
                                    values.treatmentChemo && "Chemotherapy",
                                    values.treatmentRadiation && "Radiation",
                                    values.treatmentDialysis && "Dialysis",
                                    values.treatmentSuctioning && "Suctioning",
                                    values.treatmentWoundCare && `Wound care: ${values.treatmentWoundCare}`,
                                    values.treatmentInfections && `Infection(s): ${values.treatmentInfections}`,
                                ])} />
                                <InfoRow label="Treatment Comments" value={values.treatmentComments} />
                                <InfoRow label="Self-Medication" value={values.medicationLevel || undefined} />
                                <InfoRow label="Medication Comments" value={values.medicationComments} />
                                <InfoRow label="Transportation" value={values.transportationLevel || undefined} />
                                <InfoRow label="Transportation Comments" value={values.transportationComments} />
                            </div>
                        </Card>
                    </>
                ) : (
                    <>
                        <p className="text-sm text-color-secondary mb-3 p-2" style={{ background: "#f8fafc", borderRadius: 6, border: "1px solid #e5e7eb" }}>
                            <strong>Negotiated Plan of Care:</strong> The RN must assess the needs of the client and define how care is delivered. As the provider sees subtle changes in care needs, they may make notations. The RN will review any notes during visits (typically every 60-90 days) and update as needed.
                        </p>
                        {/* Positioning */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Positioning", "Ability to move about in bed or chair, turn side-to-side, and position body for comfort.", "pi pi-arrows-alt")}
                            <div className="p-3">
                                {radioLevel("positioningLevel", SIX_LEVEL_ADL)}
                                <div className="grid mt-2">
                                    <div className="col-12 md:col-6">{txt("repositionHoursDay", "Reposition every ___ hours during day")}</div>
                                    <div className="col-12 md:col-6">{txt("repositionHoursNight", "Reposition every ___ hours during night")}</div>
                                </div>
                                <p className="font-semibold text-sm mt-2 mb-2">Special Equipment</p>
                                <div className="grid">
                                    <div className="col-6 md:col-4">{cb("positioningDrawSheet", "Draw sheet")}</div>
                                    <div className="col-6 md:col-4">{cb("positioningHospitalBed", "Hospital bed")}</div>
                                    <div className="col-6 md:col-4">{cb("positioningSpecialMattress", "Special mattress")}</div>
                                    <div className="col-6 md:col-4">{cb("positioningTrapeze", "Trapeze")}</div>
                                    <div className="col-6 md:col-4">{cb("positioningWedge", "Wedge")}</div>
                                    <div className="col-6 md:col-4">{cb("positioningFootCradle", "Foot cradle")}</div>
                                    <div className="col-6 md:col-4">{cb("positioningBedRails", "Bed rails")}</div>
                                    <div className="col-12">{txt("positioningOther", "Other equipment")}</div>
                                </div>
                                {comments("positioningComments")}
                            </div>
                        </Card>

                        {/* Transfers */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Transfers", "Ability to move to/from bed, chair, wheelchair, standing to sitting.", "pi pi-arrow-right-arrow-left")}
                            <div className="p-3">
                                {radioLevel("transfersLevel", SIX_LEVEL_ADL)}
                                {cb("transfersMechanical", "Requires mechanical lifting")}
                                {comments("transfersComments")}
                            </div>
                        </Card>

                        {/* Personal Hygiene */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Personal Hygiene", "Ability to shave, do make-up, wash hands/face/perineum, and care for hair, teeth, dentures, and hearing aids.", "pi pi-user")}
                            <div className="p-3">
                                {radioLevel("hygieneLevel", [
                                    { value: "independent", label: "Independent" },
                                    { value: "setup", label: "Requires set-up" },
                                    { value: "monitoring", label: "Requires monitoring, encouragement and/or cueing" },
                                    { value: "handsOn", label: "Able to perform but requires hands-on assistance to guide through task completion" },
                                    { value: "unable", label: "Unable to assist" },
                                ])}
                                <div className="grid mt-2">
                                    <div className="col-12 md:col-4">{txt("hygieneSkinPrecautions", "Skin precautions")}</div>
                                    <div className="col-12 md:col-4">{txt("hygieneLotions", "Lotions/soaps/linens")}</div>
                                    <div className="col-12 md:col-4">{txt("hygieneNailCare", "Nail care")}</div>
                                </div>
                                {comments("hygieneComments")}
                            </div>
                        </Card>

                        {/* Dressing */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Dressing", "Ability to put on, take off, fasten/unfasten clothing, lay out clothes and retrieve from closet.", "pi pi-tag")}
                            <div className="p-3">
                                {radioLevel("dressingLevel", [
                                    { value: "independent", label: "Dresses independently and appropriately" },
                                    { value: "setup", label: "Requires set-up" },
                                    { value: "monitoring", label: "Requires monitoring, encouragement and/or cueing" },
                                    { value: "laidOut", label: "Requires clothing to be laid out" },
                                    { value: "shoesSocks", label: "Requires help with shoes/socks/TED hose" },
                                    { value: "guideLimbs", label: "Able to assist but requires guiding of limbs and/or help with tying or buttoning" },
                                    { value: "caregiverOne", label: "Unable to assist: Caregiver must dress (1 person)" },
                                    { value: "caregiverTwo", label: "Unable to assist: Caregiver must dress (2 persons)" },
                                ])}
                                {comments("dressingComments")}
                            </div>
                        </Card>

                        {/* Ambulation */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Ambulation/Mobility", "Ability to walk, move between locations with or without assistive devices.", "pi pi-map-marker")}
                            <div className="p-3">
                                {radioLevel("ambulationLevel", [
                                    { value: "independent", label: "Walks independently without caregiver assistance or use of assistive device" },
                                    { value: "assistiveDevice", label: "Can walk but requires assistive device" },
                                    { value: "standby", label: "Can walk but requires standby assistance for safety and cueing" },
                                    { value: "steadying", label: "Can walk but requires hands-on steadying" },
                                    { value: "onePersonWeight", label: "Requires weight bearing support — 1 person" },
                                    { value: "twoPersonWeight", label: "Requires weight bearing support — 2 persons" },
                                    { value: "wheelchairIndependent", label: "Cannot walk but is mobile with wheelchair (independent)" },
                                    { value: "wheelchairAssist", label: "Cannot walk but is mobile with wheelchair (requires assistance)" },
                                    { value: "bedBound", label: "Cannot walk or use wheelchair (bed-bound)" },
                                ])}
                                {txt("ambulationDistanceLimit", "Walking distance limit (feet and reason)")}
                                {comments("ambulationComments")}
                            </div>
                        </Card>

                        {/* Stairs */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Stairs", "Ability to go up and down stairs.", "pi pi-sort-alt")}
                            <div className="p-3">
                                {radioLevel("stairsLevel", [
                                    { value: "withoutAssist", label: "Able — without assistance" },
                                    { value: "standbyAssist", label: "Able — with standby assistance" },
                                    { value: "assistiveDevice", label: "Able — with assistive device" },
                                    { value: "unableToAssess", label: "Unable to assess" },
                                    { value: "unable", label: "Unable to go up and down stairs" },
                                ])}
                                {comments("stairsComments")}
                            </div>
                        </Card>

                        {/* Assistive Devices */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Assistive Devices", "Equipment currently in use.", "pi pi-cog")}
                            <div className="p-3">
                                <div className="grid">
                                    <div className="col-6 md:col-4">{cb("deviceNone", "No equipment used")}</div>
                                    <div className="col-6 md:col-4">{cb("deviceCaneRegular", "Cane — Regular")}</div>
                                    <div className="col-6 md:col-4">{cb("deviceCaneQuad", "Cane — Quad")}</div>
                                    <div className="col-6 md:col-4">{cb("deviceCrutches", "Crutches")}</div>
                                    <div className="col-6 md:col-4">{cb("deviceGaitBelt", "Gait belt")}</div>
                                    <div className="col-6 md:col-4">{cb("deviceProsthesis", "Prosthesis")}</div>
                                    <div className="col-6 md:col-4">{cb("deviceTransportChair", "Transport chair")}</div>
                                    <div className="col-6 md:col-4">{cb("deviceWalkerTwo", "Walker — Two-wheel")}</div>
                                    <div className="col-6 md:col-4">{cb("deviceWalkerFour", "Walker — Four-wheel")}</div>
                                    <div className="col-6 md:col-4">{cb("deviceWheelchairRegular", "Wheelchair — Regular")}</div>
                                    <div className="col-6 md:col-4">{cb("deviceWheelchairElectric", "Wheelchair — Electric")}</div>
                                    <div className="col-12">{txt("deviceOther", "Other device")}</div>
                                </div>
                                {comments("deviceComments")}
                            </div>
                        </Card>

                        {/* Toileting */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Toileting", "Ability to use commode, bedpan, urinal; transfer on/off toilet, manage clothing, cleanse, change pads, manage ostomy/catheter.", "pi pi-circle")}
                            <div className="p-3">
                                {radioLevel("toiletingLevel", [
                                    { value: "independent", label: "Independent with toileting tasks" },
                                    { value: "setup", label: "Requires set-up of supplies" },
                                    { value: "monitoring", label: "Requires monitoring, encouragement and/or cueing" },
                                    { value: "assistCleansing", label: "Able to assist but requires assistance with cleansing/care/pads/clothing and/or standby for transfer" },
                                    { value: "liftingOne", label: "Able to assist but dependent in at least one task and/or requires lifting assistance (1 person)" },
                                    { value: "liftingTwo", label: "Able to assist but dependent in at least one task and/or requires lifting assistance (2 persons)" },
                                    { value: "dependentOne", label: "Unable to assist: Dependent upon assistance for all toileting tasks (1 person)" },
                                    { value: "dependentTwo", label: "Unable to assist: Dependent upon assistance for all toileting tasks (2 persons)" },
                                ])}
                                {txt("toiletingNightFrequency", "Requires toileting assistance at night — Frequency")}
                                <div className="mt-2">{cb("toiletingInappropriatePlaces", "Urinates or defecates in inappropriate places")}</div>
                                {comments("toiletingComments")}
                            </div>
                        </Card>

                        {/* Bowel Management */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Bowel Management", "Bowel routine and care.", "pi pi-circle")}
                            <div className="p-3">
                                <div className="grid">
                                    <div className="col-6 md:col-4">{cb("bowelTrainingProgram", "Training program")}</div>
                                    <div className="col-6 md:col-4">{cb("bowelEnema", "Enema")}</div>
                                    <div className="col-6 md:col-4">{cb("bowelSuppository", "Rectal suppository")}</div>
                                    <div className="col-6 md:col-4">{cb("bowelConstipation", "Constipation")}</div>
                                    <div className="col-6 md:col-4">{cb("bowelDiarrhea", "Diarrhea")}</div>
                                    <div className="col-6 md:col-4">{cb("bowelOstomy", "Ostomy")}</div>
                                    <div className="col-12">{txt("bowelMeds", "Medications")}</div>
                                </div>
                                {comments("bowelComments")}
                            </div>
                        </Card>

                        {/* Bladder Management */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Bladder Management", "Bladder routine and incontinence care.", "pi pi-circle")}
                            <div className="p-3">
                                <div className="grid">
                                    <div className="col-6 md:col-4">{cb("bladderTrainingProgram", "Training program")}</div>
                                    <div className="col-6 md:col-4">{cb("bladderDribbling", "Dribbling")}</div>
                                    <div className="col-6 md:col-4">{cb("bladderUrgency", "Urgency")}</div>
                                    <div className="col-6 md:col-4">{cb("bladderStressIncontinence", "Stress incontinence (exercising, sneezing, coughing)")}</div>
                                    <div className="col-6 md:col-4">{cb("bladderUrostomy", "Urostomy")}</div>
                                    <div className="col-6 md:col-4">{cb("bladderCatheter", "Catheter")}</div>
                                </div>
                                <p className="font-semibold text-sm mt-2 mb-1">Incontinence Care</p>
                                <div className="grid">
                                    <div className="col-6 md:col-3">{cb("bladderPadsDays", "Pads — Days")}</div>
                                    <div className="col-6 md:col-3">{cb("bladderPadsNights", "Pads — Nights")}</div>
                                    <div className="col-6 md:col-3">{cb("bladderUndergarmentsDays", "Undergarments/briefs — Days")}</div>
                                    <div className="col-6 md:col-3">{cb("bladderUndergarmentsNights", "Undergarments/briefs — Nights")}</div>
                                </div>
                                {comments("bladderComments")}
                            </div>
                        </Card>

                        {/* Bathing */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Bathing", "Ability to take bath, shower, or sponge bath; dry off; transfer in/out of tub/shower.", "pi pi-circle")}
                            <div className="p-3">
                                {radioLevel("bathingLevel", [
                                    { value: "independent", label: "Independent with bathing" },
                                    { value: "setup", label: "Requires set-up of supplies" },
                                    { value: "monitoring", label: "Requires monitoring, encouragement and/or cueing" },
                                    { value: "inOutAssist", label: "Able to bathe self but requires assistance getting in and out of tub or shower" },
                                    { value: "partialAssist", label: "Requires physical assistance with part of bathing" },
                                    { value: "fullOne", label: "Requires complete bathing assistance — 1 person" },
                                    { value: "fullTwo", label: "Requires complete bathing assistance — 2 persons" },
                                ])}
                                <p className="font-semibold text-sm mt-2 mb-2">Type</p>
                                <div className="grid mb-2">
                                    <div className="col-4">{cb("bathingTub", "Tub")}</div>
                                    <div className="col-4">{cb("bathingShower", "Shower")}</div>
                                    <div className="col-4">{cb("bathingBedBath", "Bed bath")}</div>
                                </div>
                                <p className="font-semibold text-sm mb-2">Equipment</p>
                                <div className="grid mb-2">
                                    <div className="col-6">{cb("bathingBathBench", "Bath bench")}</div>
                                    <div className="col-6">{cb("bathingTransferBench", "Transfer bench")}</div>
                                </div>
                                {txt("bathingFrequency", "Frequency")}
                                {comments("bathingComments")}
                            </div>
                        </Card>

                        {/* Nutrition */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Nutrition Needs (Eating/Drinking)", "Ability to eat/drink food/liquids, including equipment and preferences.", "pi pi-shopping-cart")}
                            <div className="p-3">
                                {radioLevel("nutritionLevel", [
                                    { value: "independent", label: "Independent" },
                                    { value: "setup", label: "Requires set-up (includes cutting up meat and opening containers)" },
                                    { value: "monitoring", label: "Requires monitoring, encouragement and/or cueing" },
                                    { value: "guideFood", label: "Able to feed self but requires hands-on assistance to guide or hand food/drink item" },
                                    { value: "partialFeed", label: "Able to feed self some foods but always needs to be fed a meal or part of a meal" },
                                    { value: "fullFeed", label: "Must be fed — dependent for all foods/fluids" },
                                ])}
                                <div className="grid mt-2">
                                    <div className="col-6 md:col-4">{cb("nutritionWeightLoss", "Weight loss")}</div>
                                    <div className="col-6 md:col-4">{cb("nutritionWeightGain", "Weight gain")}</div>
                                    <div className="col-6 md:col-4">{cb("nutritionChokingRisk", "Chewing/swallowing problems (choking, coughing, pocketing food, drooling)")}</div>
                                </div>
                                <div className="grid mt-2">
                                    <div className="col-12 md:col-6">{txt("nutritionFoodPreferences", "Food preferences")}</div>
                                    <div className="col-12 md:col-6">{txt("nutritionFoodAllergies", "Food allergies")}</div>
                                    <div className="col-12 md:col-4">{txt("nutritionSpecialDiet", "Special diet (Therapeutic)")}</div>
                                    <div className="col-12 md:col-4">{txt("nutritionSupplements", "Supplements")}</div>
                                    <div className="col-12 md:col-4">{txt("nutritionMechAltered", "Mechanically altered")}</div>
                                    <div className="col-12">{txt("nutritionAdaptiveEquipment", "Adaptive equipment")}</div>
                                </div>
                                {comments("nutritionComments")}
                            </div>
                        </Card>

                        {/* Therapies */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Therapies", "Current therapeutic services.", "pi pi-heart-fill")}
                            <div className="p-3">
                                <div className="grid">
                                    <div className="col-6 md:col-4">{cb("therapySpeech", "Speech")}</div>
                                    <div className="col-6 md:col-4">{cb("therapyPhysical", "Physical")}</div>
                                    <div className="col-6 md:col-4">{cb("therapyOccupational", "Occupational")}</div>
                                    <div className="col-6 md:col-4">{cb("therapyMentalHealth", "Mental health")}</div>
                                    <div className="col-6 md:col-4">{cb("therapySkilledNursing", "Skilled nursing")}</div>
                                    <div className="col-6 md:col-4">{cb("therapyHospice", "Hospice")}</div>
                                    <div className="col-6 md:col-4">{cb("therapyRespiratory", "Respiratory (nebulizer/inhaler)")}</div>
                                    <div className="col-6 md:col-4">{cb("therapyCardiovascular", "Cardiovascular (PT/INR)")}</div>
                                    <div className="col-6 md:col-4">{cb("therapyStrengthROM", "Strength/range of motion")}</div>
                                    <div className="col-6 md:col-4">{cb("therapyPressureUlcers", "Pressure ulcers")}</div>
                                    <div className="col-12 md:col-6">{txt("therapyPainManagement", "Pain management details")}</div>
                                    <div className="col-12 md:col-6">{txt("therapyOther", "Other therapies")}</div>
                                </div>
                                {comments("therapyComments")}
                            </div>
                        </Card>

                        {/* Medical Treatment */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Medical Treatment", "Current medical treatments and procedures.", "pi pi-plus-circle")}
                            <div className="p-3">
                                <div className="grid">
                                    <div className="col-6 md:col-4">{cb("treatmentNone", "No medical treatment")}</div>
                                    <div className="col-6 md:col-4">{cb("treatmentFeedingTube", "Feeding tube")}</div>
                                    <div className="col-6 md:col-4">{cb("treatmentChemo", "Chemotherapy")}</div>
                                    <div className="col-6 md:col-4">{cb("treatmentRadiation", "Radiation")}</div>
                                    <div className="col-6 md:col-4">{cb("treatmentDialysis", "Dialysis")}</div>
                                    <div className="col-6 md:col-4">{cb("treatmentSuctioning", "Suctioning")}</div>
                                    <div className="col-12 md:col-6">{txt("treatmentWoundCare", "Wound care details")}</div>
                                    <div className="col-12 md:col-6">{txt("treatmentInfections", "Infection(s)")}</div>
                                </div>
                                {comments("treatmentComments")}
                            </div>
                        </Card>

                        {/* Self-Medication */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Self-Medication Administration", "Ability to take own medication safely and reliably.", "pi pi-file-edit")}
                            <div className="p-3">
                                {radioLevel("medicationLevel", [
                                    { value: "selfAdmin", label: "All medications are independently self-administered" },
                                    { value: "requiresAssist", label: "Client requires assistance with one or more medications" },
                                    { value: "caregiverAdmin", label: "Caregiver administration required for one or more medications" },
                                ])}
                                {comments("medicationComments")}
                            </div>
                        </Card>

                        {/* Transportation */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Transportation/Appointments", "Ability to arrange and attend appointments.", "pi pi-car")}
                            <div className="p-3">
                                {radioLevel("transportationLevel", [
                                    { value: "independent", label: "Independent with respect to transportation and making appointments" },
                                    { value: "requiresAssist", label: "Requires assistance setting up appointments or arranging transportation" },
                                ])}
                                {comments("transportationComments")}
                            </div>
                        </Card>

                    </>
                )}
                <div className="flex justify-content-center gap-2 mt-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>
            </form>
        </>
    );
};

export default Step11ActivitiesOfDailyLiving;
